/**
 * Shared local-storage feature-flag convention for the dictation (speech-to-text) feature.
 *
 * A single storage key is used by both `HxTextarea` (to decide whether to render the microphone icon) and the Shell
 * application's profile menu / system-debug page (to let the user toggle the feature) — do not duplicate this key
 * elsewhere.
 */

/** LocalStorage key controlling whether dictation is enabled. */
export const DICTATION_ENABLED_STORAGE_KEY = 'symphony.dictation.enabled';

/**
 * Custom event dispatched whenever the dictation setting changes in the current tab. The native `storage` event only
 * fires in OTHER tabs, so components that cache this flag should listen for both.
 */
export const DICTATION_ENABLED_CHANGED_EVENT = 'symphony:dictation-enabled-changed';

export interface DictationEnabledChangedDetail {
  enabled: boolean;
}

/** Reads the current dictation feature flag from localStorage. Safe to call during SSR (always returns false). */
export function isDictationFeatureEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(DICTATION_ENABLED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persists the dictation feature flag and notifies listeners in the current tab (e.g. `HxTextarea` instances). */
export function setDictationFeatureEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(DICTATION_ENABLED_STORAGE_KEY, String(enabled));
  } catch {
    // Ignore storage write failures (private browsing mode, quota exceeded, etc.)
  }
  window.dispatchEvent(
    new CustomEvent<DictationEnabledChangedDetail>(DICTATION_ENABLED_CHANGED_EVENT, { detail: { enabled } })
  );
}
